<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { DocumentsApi } from '../services/documentsApi'
import type { DocumentAudioKind, DocumentText } from '../types/document.types'

const props = defineProps<{ documentId: string }>()
const kind = ref<DocumentAudioKind>('summary')
const result = ref<DocumentText | null>(null)
const loading = ref(false)
const failed = ref(false)
let request: AbortController | null = null

async function load() {
  request?.abort()
  const current = new AbortController()
  request = current
  result.value = null
  failed.value = false
  loading.value = true
  try {
    const data = await DocumentsApi.fetchText(props.documentId, kind.value, current.signal)
    if (!current.signal.aborted) result.value = data
  } catch {
    if (!current.signal.aborted) failed.value = true
  } finally {
    if (!current.signal.aborted) loading.value = false
  }
}

watch([() => props.documentId, kind], load, { immediate: true })
onBeforeUnmount(() => request?.abort())
</script>

<template>
  <div class="document-texts">
    <VTabs
      v-model="kind"
      grow
      color="primary"
      aria-label="Версия текста"
    >
      <VTab value="summary">
        Аннотация
      </VTab>
      <VTab value="full">
        Полный текст
      </VTab>
    </VTabs>
    <div class="document-texts__toolbar">
      <span class="text-caption text-medium-emphasis">
        {{ result?.edition ? `Редакция ${result.edition}` : 'Текущая редакция' }}
      </span>
    </div>
    <div
      class="document-texts__body"
      role="tabpanel"
      tabindex="0"
      :aria-label="kind === 'summary' ? 'Аннотация' : 'Полный текст'"
      :aria-busy="loading"
    >
      <div v-if="loading" class="py-8 text-center" role="status">
        <VProgressCircular indeterminate size="24" class="mb-3" />
        <div>Загрузка текста…</div>
      </div>
      <VAlert v-else-if="failed" type="error" variant="tonal">
        Не удалось загрузить текст. Закройте и снова откройте панель «Тексты документа», чтобы повторить.
      </VAlert>
      <VAlert v-else-if="!result?.text?.trim()" type="info" variant="tonal">
        {{ result?.version_id == null
          ? 'У документа пока нет файла.'
          : 'Текст находится в обработке' }}
      </VAlert>
      <template v-else>
        <div class="text-caption text-medium-emphasis mb-4">
          {{ kind === 'summary' ? 'Аннотация подготовлена ИИ. Сверяйте важные сведения с оригиналом.' : 'Текст извлечён из документа. Исходное оформление может отличаться.' }}
        </div>
        <div class="document-texts__text">
          {{ result.text }}
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.document-texts {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.document-texts__toolbar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  gap: 8px;
}
.document-texts :deep(.v-tabs) {
  flex: 0 0 auto;
}
.document-texts__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 24px 24px;
}
.document-texts__text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.7;
  font-size: 14px;
}
</style>
